/**
 * Optional helper for explicit no-cache queries.
 * By default, all admin queries already use no cache (via global defaults).
 * Use this when you want to explicitly ensure no cache in a hook that might
 * inherit options from elsewhere.
 */

import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';

/**
 * useNoCacheQuery - Use query with explicit no-cache settings.
 * Mirrors useQuery API but guarantees fresh data on every mount/focus.
 */
export function useNoCacheQuery<
  TQueryFnData = unknown,
  TError = Error,
  TData = TQueryFnData,
  TQueryKey extends readonly unknown[] = readonly unknown[]
>(
  options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>
): UseQueryResult<TData, TError> {
  return useQuery({
    ...options,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });
}
